import { createGlobalStyle } from 'styled-components'
import OpenSansBold from '../assets/fonts/OpenSans-Bold.woff2'
import OpenSansItalic from '../assets/fonts/OpenSans-Italic.woff2'
import OpenSans from '../assets/fonts/OpenSans-Regular.woff2'

const GlobalStyle = createGlobalStyle`
  @font-face {
      font-family: 'Open Sans';
      src: url(${OpenSansItalic}) format('woff2');
      font-weight: normal;
      font-style: italic;
      font-display: swap;
  }

  @font-face {
      font-family: 'Open Sans';
      src: url(${OpenSansBold}) format('woff2');
      font-weight: bold;
      font-style: normal;
      font-display: swap;
  }

  @font-face {
      font-family: 'Open Sans';
      src: url(${OpenSans}) format('woff2');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
  }


  html {
    --color-celery-400: #3da74e;
    --color-celery-500: #44b556;
    --color-celery-600: #4bc35f;
    --color-celery-700: #51d267;
    --color-chartreuse-400: #7cc33f;
    --color-chartreuse-500: #85d044;
    --color-chartreuse-600: #8ede49;
    --color-chartreuse-700: #9bec54;
    --color-yellow-400: #d2b200;
    --color-yellow-500: #dfbf00;
    --color-yellow-600: #edcc00;
    --color-yellow-700: #fad900;
    --color-magenta-400: #ca2996;
    --color-magenta-500: #d83790;
    --color-magenta-600: #e2499d;
    --color-magenta-700: #ec5aaa;
    --color-fuchsia-400: #b130bd;
    --color-fuchsia-500: #c038cc;
    --color-fuchsia-600: #cf3edc;
    --color-fuchsia-700: #d951e5;
    --color-purple-400: #864ccc;
    --color-purple-500: #9256d9;
    --color-purple-600: #9d64e1;
    --color-purple-700: #a873df;
    --color-indigo-400: #5c5ce0;
    --color-indigo-500: #6767ec;
    --color-indigo-600: #7575f1;
    --color-indigo-700: #8282f6;
    --color-seafoam-400: #16878c;
    --color-seafoam-500: #1b959a;
    --color-seafoam-600: #20a3a8;
    --color-seafoam-700: #23b2b8;
    --color-red-400: #d7373f;
    --color-red-500: #e34850;
    --color-red-600: #ec5b62;
    --color-red-700: #f76d74;
    --color-orange-400: #da7b11;
    --color-orange-500: #e68619;
    --color-orange-600: #f29423;
    --color-orange-700: #f9a43f;
    --color-green-400: #268e6c;
    --color-green-500: #2d9d78;
    --color-green-600: #33ab84;
    --color-green-700: #39b990;
    --color-blue-400: #1473e6;
    --color-blue-500: #2680eb;
    --color-blue-600: #378ef0;
    --color-blue-700: #4b9cf5;
    --color-black: #000;
    --color-gray-50: #080808;
    --color-gray-75: #1a1a1a;
    --color-gray-100: #1e1e1e;
    --color-gray-200: #2c2c2c;
    --color-gray-300: #393939;
    --color-gray-400: #494949;
    --color-gray-500: #5c5c5c;
    --color-gray-600: #7c7c7c;
    --color-gray-700: #a2a2a2;
    --color-gray-800: #c8c8c8;
    --color-gray-900: #efefef;
    --color-white: #fff;
    --radius: 4px;
    --primary-text-color: var(--color-gray-700);

    box-sizing: border-box;
    font-size: 100%;
    line-sizing: normal;
    overflow-x: hidden;
    text-spacing: trim-start allow-end trim-adjacent ideograph-alpha ideograph-numeric;
    touch-action: manipulation;
    -webkit-text-size-adjust: 100%;

    /* @media (min-width: 68.75em) { */
    @media (min-width: 48em) {
      font-size: 125%;
    }

    @supports (font-kerning: normal) and (font-varient-ligatures: common-ligatures contextual) and (font-variant-numeric: oldstyle-nums proportional-nums) {
      font-feature-settings: normal;
      font-kerning: normal;
      font-variant-ligatures: common-ligatures contextual;
      font-variant-numeric: oldstyl-nums proportional-nums;
    }
  }

  *, *:before, *:after {
    box-sizing: inherit;
  }

  body {
    background-color: var(--color-background);
    color: var(--color-foreground);
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-weight: 300;
    margin: 0;
    padding: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    line-height: 1.1;
    margin-top: 0;
  }

  /* img {
    width: 100%;
  } */

  a {
    color: var(--color-blue-400);
    transition: color 113ms ease-in-out;

    &:visited {
      color: var(--color-purple-400);
    }

    &:focus, &:hover {
      color: var(--color-seafoam-400);
    }

    &:active {
      color: var(--color-red-400);
    }
  }

  abbr {
    font-feature-settings: 'kern', 'liga', 'clig', 'calt', 'c2sc', 'smcp';
    @supports (font-variant-caps: all-small-caps) {
      font-feature-settings: normal;
      font-variant-caps: all-small-caps;
    }
  }

  [hidden] { display: none !important; }

  .visually-hidden {
    clip: rect(1px, 1px, 1px 1px);
    height: 1px;
    overflow: hidden;
    position: absolute;
    visibility: hidden;
    width: 1px;
  }

  .no-break { hyphens: none; }

  .numbers { letter-spacing: .01em; }

  blockquote {
    border-left: 0.625rem solid var(--color-red);
    font-size: 1.1rem;
    margin: 0;
    padding-left: 1.5rem;
  }

  .shroud {
    background-color: rgba(0,0,0,0.6);
    height: 100vh;
    left: 0;
    position: fixed;
    top: 0;
    width: 100vw;
    z-index: 100;
  }

  .locked {
    overflow: hidden;
    max-height: 100vh;
  }

  .text-center {
    text-align: center;
  }

  pre {
    background-color: var(--color-gray-900);
    font-size: .9rem;
    overflow-x: scroll;
    padding: .5rem;
  }
`

export default GlobalStyle
